import React, { useState } from 'react';
import {
  Box,
  Button,
  Popover,
  Typography,
  CircularProgress,
  Theme,
} from '@mui/material';
import { createStyles, makeStyles } from '@material-ui/core';
import PersonOutlinedIcon from '@material-ui/icons/PersonOutlined';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { StyledButton } from './_components';
import { clearLinkedUserId } from '../../utils/persistentUserId';
import { clearUserCookie } from '../../utils/userCookie';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      color: 'rgba(255, 255, 255, 0.70)',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'row-reverse',
        columnGap: '8px',
        color: 'rgba(255, 255, 255, 0.70)',
        fontFamily: 'Inter',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: 'normal',
        padding: '15px',
      },
    },
    popper: {
      padding: 8,
      backgroundColor: 'white',
      borderRadius: '4px',
    },
  })
);

interface AuthButtonProps {
  /** Render as a button instead of inline text */
  button?: boolean;
  /** Use main site login flow instead of direct Auth0 */
  useMainSiteLogin?: boolean;
}

const AuthButton: React.FC<AuthButtonProps> = ({
  button = false,
  useMainSiteLogin = false,
}) => {
  const classes = useStyles();
  const {
    isAuthenticated,
    isLoading,
    email,
    name,
    login,
    loginViaMainSite,
    logout: auth0Logout,
  } = useAuth();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const history = useHistory();

  const logout = () => {
    clearLinkedUserId();
    clearUserCookie();

    if (isAuthenticated) {
      auth0Logout();
    } else {
      window.location.reload();
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isAuthenticated) {
      setAnchorEl(anchorEl ? null : event.currentTarget);
    } else {
      if (useMainSiteLogin) {
        loginViaMainSite();
      } else {
        login();
      }
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'auth-popover' : undefined;

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" px={1}>
        <CircularProgress size={20} sx={{ color: 'rgba(255, 255, 255, 0.70)' }} />
      </Box>
    );
  }

  // Button variant (used in LoginRegisterContainer)
  if (button) {
    if (isAuthenticated) {
      return (
        <>
          <StyledButton
            size="medium"
            variant="contained"
            onClick={handleClick}
          >
            {name || email}
          </StyledButton>
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <div className={classes.popper}>
              <Button onClick={handleLogout}>Logout</Button>
            </div>
          </Popover>
        </>
      );
    }
    return (
      <StyledButton
        size="medium"
        variant="contained"
        onClick={handleClick}
        style={{ minWidth: 'auto' }}
      >
        Log in
      </StyledButton>
    );
  }

  // Inline variant (used in header)
  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        style={{ cursor: 'pointer' }}
        onClick={handleClick}
        className={classes.container}
      >
        {isAuthenticated ? <CheckCircleOutlineIcon /> : <PersonOutlinedIcon />}
        <Box marginLeft="4px" style={{ whiteSpace: 'nowrap' }}>
          {isAuthenticated ? 'Logged In' : 'Sign in'}
        </Box>
      </Box>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box p={2} minWidth={200}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {email}
          </Typography>
          <Button fullWidth variant="outlined" size="small" onClick={handleLogout}>
            Sign Out
          </Button>
          <Button
            fullWidth
            variant="text"
            size="small"
            onClick={() => { handleClose(); history.push('/account'); }}
            sx={{ mt: 1, textTransform: 'none' }}
          >
            My Account
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default AuthButton;
